import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockContainer from '../components/GuideMockContainer';
import GuideMockListHeader from '../components/GuideMockListHeader';
import GuideMockSelect from '../components/GuideMockSelect';
import GuideMockTextField from '../components/GuideMockTextField';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideProjectRegister: React.FC = () => {
  return (
    <GuidePageLayout
      title="新規プロジェクトを登録する"
      description="新しいプロジェクトを登録し、取引先・マネージャーと紐付けます。"
      breadcrumbs={[{ label: 'プロジェクト管理', path: '/guide/project/overview' }]}
    >
      <GuideSection title="前提">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理者 / 管理部門 / 課長'],
            ['ログイン状態', 'ログイン済みであること'],
            ['事前準備', '取引先が登録済みであること'],
          ]}
        />
      </GuideSection>

      <GuideSection title="手順">
        <GuideStepList
          steps={[
            { label: '要員管理メニューからプロジェクト管理画面を開く' },
            {
              label: '一覧パネル右上の「+」アイコンを押下して登録フォームを開く',
              content: (
                <GuideMockContainer label="リストヘッダー">
                  <GuideMockListHeader createLabel="プロジェクト新規作成" />
                </GuideMockContainer>
              ),
            },
            {
              label: 'プロジェクト情報を入力する',
              description:
                'フォームタイトルは「プロジェクト登録」です。タイトル左の戻る矢印をクリックすると、登録を破棄して一覧に戻ります。',
              content: (
                <GuideMockContainer label="登録フォーム">
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ pointerEvents: 'none' }}
                    >
                      <IconButton size="small">
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="h6" fontWeight={700}>
                        プロジェクト登録
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid size={6}>
                        <GuideMockTextField label="プロジェクト名" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="プロジェクト名カナ" required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect label="課長" options={['山田太郎', '鈴木一郎']} required />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect
                          label="取引先"
                          options={['サンプル株式会社', 'テスト企業']}
                          required
                        />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect label="規模" options={['小規模', '中規模', '大規模']} />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockSelect label="商流" options={['一次請け', '二次請け']} />
                      </Grid>
                      <Grid size={6}>
                        <GuideMockTextField label="開始日（年度月選択）" />
                      </Grid>
                      <Grid size={12}>
                        <GuideMockTextField label="備考（6行表示）" />
                      </Grid>
                    </Grid>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '「作成」ボタンを押下する',
              content: (
                <GuideMockContainer label="フッターボタン">
                  <GuideMockButton label="作成" startIcon={<AddIcon />} />
                </GuideMockContainer>
              ),
            },
          ]}
        />
      </GuideSection>

      <GuideSection title="入力項目">
        <GuideTable
          headers={['項目名', '必須', '形式', '備考']}
          rows={[
            ['プロジェクト名', '必須', 'テキスト（最大300桁）', ''],
            ['プロジェクト名カナ', '必須', 'テキスト（最大300桁）', 'カタカナのみ'],
            ['課長', '必須', 'プルダウン', '課長権限を持つユーザーから選択'],
            ['取引先', '必須', 'プルダウン', '登録済み取引先から選択'],
            ['規模', '任意', 'ラジオボタン', 'マスタ区分から選択'],
            ['商流', '任意', 'ラジオボタン', 'マスタ区分から選択'],
            ['業界', '任意', '複数選択', 'マスタ区分から選択'],
            ['技術スタック', '任意', '複数選択', '登録済み技術スタックから選択'],
            ['開始日', '任意', '年度月選択', 'FinancialYearMonthSelectで選択'],
            ['備考', '任意', 'テキストエリア（最大10000桁）', '6行表示'],
          ]}
        />
      </GuideSection>

      <GuideSection title="ボタンと操作">
        <GuideTable
          headers={['操作', '動作']}
          rows={[
            ['← 戻る矢印（タイトル左）', '登録を破棄して一覧画面に戻る'],
            ['作成ボタン', '入力内容でプロジェクトを登録する'],
          ]}
        />
      </GuideSection>

      <GuideSection title="注意事項">
        <Stack spacing={1}>
          <GuideInfoBox>プロジェクトIDは自動的に発行されます。</GuideInfoBox>
          <GuideInfoBox>
            取引先は事前に登録されている必要があります（取引先管理画面で登録）。
          </GuideInfoBox>
          <GuideInfoBox>課長権限を持つユーザーのみ選択可能です。</GuideInfoBox>
          <GuideInfoBox>
            開始日は年度月選択（FinancialYearMonthSelect）から選択します。新規作成時は終了日は入力できません。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideProjectRegister;
